import { GAME_MAPS } from '../game/maps';
import { formatTimeLimit, TIME_LIMITS, type GameSettings, type TimeLimit } from '../game/settings';
import type { PlayerStats } from '../game/stats';
import { PROVIDERS, type ProviderId } from '../providers';
import { IconCamera, IconClock, IconGlobe, IconInfo, IconMap, IconMove, IconPlay } from './Icons';
import StatsPanel from './StatsPanel';

interface HomeScreenProps {
  settings: GameSettings;
  onChange: (settings: GameSettings) => void;
  onPlay: () => void;
  stats: PlayerStats;
  onClearStats: () => void;
}

const MAP_EMOJI: Record<string, string> = {
  world: '🌍',
  europe: '🏰',
  france: '🥖',
  americas: '🗽',
  'asia-oceania': '🏯',
  africa: '🦁',
};

export default function HomeScreen({ settings, onChange, onPlay, stats, onClearStats }: HomeScreenProps) {
  const provider = PROVIDERS.find((p) => p.id === settings.providerId) ?? PROVIDERS[PROVIDERS.length - 1];
  const update = (patch: Partial<GameSettings>) => onChange({ ...settings, ...patch });

  const selectProvider = (id: ProviderId) => {
    const next = PROVIDERS.find((p) => p.id === id);
    if (!next?.isConfigured()) return;
    update({ providerId: id, mapId: next.supportedMaps.includes(settings.mapId) ? settings.mapId : 'world' });
  };

  return (
    <main className="home">
      <div className="home__bg" aria-hidden="true" />
      <div className="home__inner">
        <header className="hero">
          <div className="brand brand--lg">
            <span className="brand__logo">
              <IconGlobe size={34} />
            </span>
            <span className="brand__name">
              Geo<span>Gessr</span>
            </span>
          </div>
          <p className="hero__tagline">
            Vous êtes déposé quelque part sur Terre. Observez le panorama, repérez les indices et placez votre marqueur sur la carte.
            5 manches, 25 000 points en jeu.
          </p>
          <button type="button" className="btn btn--primary btn--xl hero__play" onClick={onPlay}>
            <IconPlay size={22} /> Jouer
          </button>
          <p className="hero__summary">
            {GAME_MAPS.find((m) => m.id === settings.mapId)?.name} · {provider.name} · {formatTimeLimit(settings.timeLimit)} ·{' '}
            {settings.allowMove ? 'Déplacements autorisés' : 'Sans déplacement'}
          </p>
        </header>

        <div className="home__grid">
          <div className="home__col">
            <section className="panel" aria-labelledby="maps-title">
              <h2 id="maps-title" className="panel__title">
                <IconMap size={18} /> Carte
              </h2>
              <div className="choice-grid" role="radiogroup" aria-labelledby="maps-title">
                {GAME_MAPS.map((m) => {
                  const supported = provider.supportedMaps.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="radio"
                      aria-checked={settings.mapId === m.id}
                      className={`choice ${settings.mapId === m.id ? 'choice--selected' : ''}`}
                      disabled={!supported}
                      onClick={() => update({ mapId: m.id })}
                      title={supported ? m.description : `Non disponible avec ${provider.name} (couverture insuffisante)`}
                    >
                      <span className="choice__emoji" aria-hidden="true">
                        {MAP_EMOJI[m.id]}
                      </span>
                      <span className="choice__name">{m.name}</span>
                      <span className="choice__desc">{supported ? m.description : `Indisponible avec ${provider.name}`}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="panel" aria-labelledby="source-title">
              <h2 id="source-title" className="panel__title">
                <IconCamera size={18} /> Source des panoramas
              </h2>
              <div className="provider-list" role="radiogroup" aria-labelledby="source-title">
                {PROVIDERS.map((p) => {
                  const configured = p.isConfigured();
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={settings.providerId === p.id}
                      className={`provider ${settings.providerId === p.id ? 'provider--selected' : ''}`}
                      disabled={!configured}
                      onClick={() => selectProvider(p.id)}
                    >
                      <span className="provider__head">
                        <span className="provider__name">{p.name}</span>
                        <span className={`tag ${configured ? 'tag--ok' : 'tag--off'}`}>
                          {configured ? (p.envVar ? 'Configurée' : 'Sans clé') : 'Clé requise'}
                        </span>
                      </span>
                      <span className="provider__desc">{p.description}</span>
                      {!configured && p.envVar && (
                        <span className="provider__hint">
                          Définissez <code>{p.envVar}</code> au moment du build (voir le README).
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="panel panel--row" aria-label="Options de jeu">
              <div className="option">
                <h2 className="panel__title" id="time-title">
                  <IconClock size={18} /> Temps par manche
                </h2>
                <div className="segmented" role="radiogroup" aria-labelledby="time-title">
                  {TIME_LIMITS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={settings.timeLimit === t}
                      className={`segmented__item ${settings.timeLimit === t ? 'segmented__item--selected' : ''}`}
                      onClick={() => update({ timeLimit: t as TimeLimit })}
                    >
                      {formatTimeLimit(t)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="option">
                <h2 className="panel__title" id="move-title">
                  <IconMove size={18} /> Déplacements
                </h2>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.allowMove}
                    onChange={(e) => update({ allowMove: e.target.checked })}
                    aria-labelledby="move-title"
                  />
                  <span className="switch__track" aria-hidden="true">
                    <span className="switch__thumb" />
                  </span>
                  <span>{settings.allowMove ? 'Autorisés : avancez le long de la route' : 'Interdits : regardez autour de vous seulement'}</span>
                </label>
              </div>
            </section>
          </div>

          <div className="home__col">
            <StatsPanel stats={stats} onClear={onClearStats} />

            <section className="panel" aria-labelledby="howto-title">
              <h2 id="howto-title" className="panel__title">
                <IconInfo size={18} /> Comment jouer
              </h2>
              <ol className="steps">
                <li>
                  <strong>Explorez</strong> le panorama à 360° : faites glisser pour tourner, molette ou pincement pour zoomer
                  {settings.allowMove ? ', flèches au sol ou clic sur la route pour vous y déplacer' : ''}.
                </li>
                <li>
                  <strong>Cherchez des indices</strong> : panneaux, langue, sens de circulation, végétation, architecture, plaques…
                </li>
                <li>
                  <strong>Placez votre marqueur</strong> sur la carte, puis validez (touche Entrée).
                </li>
                <li>
                  <strong>Marquez des points</strong> : jusqu’à 5 000 par manche, d’autant plus que vous êtes proche.
                </li>
              </ol>
            </section>
          </div>
        </div>

        <footer className="home__footer">
          Panoramas : Google Street View, Mapillary ou Panoramax selon la configuration · Cartes ©{' '}
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
            OpenStreetMap
          </a>{' '}
          · Code source sur{' '}
          <a href="https://github.com/Tipiltheglitcher/geogessr" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </footer>
      </div>
    </main>
  );
}
